import axios from 'axios';

// Create axios instance with relative URLs
const api = axios.create({
  baseURL: '', // Use relative URLs
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
});

const SESSION_EXPIRED_KEY = 'sessionExpired';

/** Clear client session and send the browser to /login on 401/403 (e.g. for manual fetch handling). */
export function redirectToLoginIfUnauthorized(status) {
  if (status !== 401 && status !== 403) return;
  const hadSession =
    !!localStorage.getItem('sessionId') ||
    /(?:^|;\s*)session=/.test(document.cookie) ||
    /(?:^|;\s*)user=/.test(document.cookie);
  localStorage.removeItem('sessionId');
  document.cookie = 'session=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
  document.cookie = 'user=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
  if (window.location.pathname !== '/login') {
    if (hadSession) {
      try {
        sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
      } catch {
        /* ignore */
      }
    }
    window.location.href = '/login';
  }
}

/** Show a one-shot session-expired toast after redirect from 401/403. */
export function consumeSessionExpiredToast() {
  try {
    if (sessionStorage.getItem(SESSION_EXPIRED_KEY) !== '1') return false;
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return true;
  } catch {
    return false;
  }
}

// Add request interceptor to include session ID in Authorization header
api.interceptors.request.use(
  (config) => {
    // Get session_id from localStorage (set after login)
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) {
      config.headers.Authorization = `Bearer ${sessionId}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      redirectToLoginIfUnauthorized(status);
    }
    return Promise.reject(error);
  }
);

export { api };
export const authService = {
  async login(email, password) {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const response = await api.post('/api/v2/auth/login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true, // Include cookies in request
    });
    
    // Store session_id from response for Authorization header
    if (response.data.session_id) {
      localStorage.setItem('sessionId', response.data.session_id);
    }
    
    return response.data;
  },

  async getCurrentUser() {
    const response = await api.get('/api/v2/auth/me');
    return response.data;
  },

  async logout() {
    try {
      // Call logout endpoint - cookie and Authorization header will be sent automatically
      await api.post('/api/v2/auth/logout', {}, {
        withCredentials: true,
      });
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    }
    // Clear session_id from localStorage
    localStorage.removeItem('sessionId');
    // Clear cookies (including any admin "view as role" override)
    document.cookie = 'session=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    document.cookie = 'user=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    document.cookie = 'cube_role_override=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
  },

  async verify2FALogin(email, code) {
    const response = await api.post('/api/v2/auth/verify-2fa-login', { email, code }, {
      withCredentials: true, // Include cookies in request
    });
    
    // Store session_id from response for Authorization header
    if (response.data.session_id) {
      localStorage.setItem('sessionId', response.data.session_id);
    }
    
    return response.data;
  },

  async updateProfile(profileData) {
    const response = await api.put('/api/v2/users/me', profileData);
    return response.data;
  },

  async changePassword(passwordData) {
    const response = await api.put('/api/v2/users/me/password', passwordData);
    return response.data;
  },

  async toggleTwoFA() {
    const response = await api.put('/api/v2/users/me/two-fa');
    return response.data;
  },

  async updateShowWelcome(showWelcome) {
    const response = await api.put('/api/v2/users/me/show-welcome', {
      show_welcome: showWelcome,
    });
    return response.data;
  },
};
